import { DestroyRef, Injectable, computed, inject } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter, tap } from 'rxjs/operators';
import { BUSINESSUNIT_STORE, QUEUE_STORE, USER_STORE } from '@dxp/core/stores';
import { AggregationLevel } from '@dxp/core/types';

export interface ISegment {
    id: string;
    label: string;
    url: string;
    level: AggregationLevel;
}

@Injectable({
    providedIn: 'root',
})
export class BreadcrumbService {
    readonly #destroyRef = inject(DestroyRef);
    readonly #router = inject(Router);
    readonly #userStore = inject(USER_STORE);
    readonly #businessUnitStore = inject(BUSINESSUNIT_STORE);
    readonly #queueStore = inject(QUEUE_STORE);

    readonly segmentsSubject = new BehaviorSubject<ISegment[]>([]);
    readonly segments = toSignal(this.segmentsSubject.asObservable());
    readonly current = computed<ISegment>(() => {
        const segments = this.segments();
        return segments && segments.length > 0
            ? segments[segments.length - 1]
            : { id: '', label: '', url: '', level: AggregationLevel.None };
    });

    constructor() {
        this.updateSegments();

        this.#router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                tap(() => this.updateSegments()),
                takeUntilDestroyed(this.#destroyRef),
            )
            .subscribe();
    }

    updateSegments() {
        const root = this.#router.routerState.snapshot.root;
        const segments: ISegment[] = [];
        this.#addSegment(root, '', segments, AggregationLevel.None);
        this.segmentsSubject.next(segments);
    }

    #addSegment(
        route: ActivatedRouteSnapshot,
        url: string,
        segments: ISegment[],
        level: AggregationLevel,
    ) {
        if (route) {
            route.url.forEach(segment => {
                if (segment.path) {
                    level++;
                    url += `/${segment.path}`;
                    segments.push({
                        id: segment.path,
                        label: this.#getBreadcrumbLabel(level, segment.path),
                        url,
                        level,
                    });
                }
            });

            if (route.children.length > 0) {
                route.children.forEach(child => {
                    this.#addSegment(child, url, segments, level);
                });
            }
        }
    }

    #getBreadcrumbLabel(level: AggregationLevel, id: string): string {
        switch (level) {
            case AggregationLevel.Tenant:
                return this.#userStore.user().tenant?.name ?? '';

            case AggregationLevel.BusinessUnit:
                return (
                    this.#businessUnitStore
                        .businessUnits()
                        ?.find(bu => bu.id === id)?.name ?? ''
                );

            case AggregationLevel.Queue:
                return (
                    this.#queueStore.queues().find(q => q.id === id)?.name ?? ''
                );

            default:
                return '';
        }
    }
}
